// re-export all types and utilities
export { formatResult } from "./lib/format"
export { mulberry32 } from "./lib/rng"
export type {
  GPConfig,
  GPHooks,
  GPMetrics,
  GPProblem,
  GPAsyncContext,
  MaybePromise,
  GPResult,
  OnGeneration,
  RNG,
  Selector,
  GPProgress,
  StopReason,
  GenerationInfo,
  GenerationStartInfo,
  IterationInfo,
} from "./types"
// first-class public types from estimation utilities
export type { CostModel, RunEstimate } from "./lib/estimate"

// engine exports
export { GPLite } from "./core/engine"
export { GPLiteAsync } from "./core/engine-async"
export { tournament } from "./core/selection"

// error utilities (opt-in)
export {
  ConfigError,
  EvolutionError,
  GPLiteError,
  ProblemError,
} from "./lib/errors"
export { validateConfig, validateProblem } from "./lib/validation"

// estimator utilities (generic cost/time projection)
export { estimateFromMetrics, estimateRun } from "./lib/estimate"
// tiny built-in logger and progress tracker
export { progressLogger, createProgressTracker } from "./lib/progress"
export type { ProgressSnapshot } from "./lib/progress"
export { createProblem } from "./lib/problem"
// Optional: expose library defaults for discoverability
export * as defaults from "./lib/defaults"

// Minimal entrypoint: stable, typed signature
import type { GPResult, RNG } from "./types"
import { GPLite } from "./core/engine"
import { DEFAULT_CX_PROB } from "./lib/defaults"
import { ProblemError } from "./lib/errors"

export function run<T>(args: {
  /** Fixed initial population of candidate genomes */
  initialPopulation: ReadonlyArray<T>
  /** Fitness score: higher is better (must be finite) */
  fitness: (g: Readonly<T>) => number
  /** Return a mutated copy of the genome */
  mutate: (g: Readonly<T>, rng: RNG) => T
  /** Optional: create a fresh random genome (required if immigration > 0) */
  createRandom?: (rng: RNG) => T
  /** Optional: crossover operator for children */
  crossover?: (a: Readonly<T>, b: Readonly<T>, rng: RNG) => T[]
  /** Number of generations to run */
  iterations: number
  /** Optional: crossover probability (default 0 if no crossover given) */
  cxProb?: number
  /** Optional: mutation probability (default 1 for minimal API) */
  mutProb?: number
  /** Optional: immigrants share per generation (0..1). Requires createRandom. */
  immigration?: number
}): GPResult<T> {
  const {
    initialPopulation,
    fitness,
    mutate,
    iterations,
    createRandom,
    crossover,
    cxProb,
    mutProb,
    immigration,
  } = args
  if (!Array.isArray(initialPopulation) || initialPopulation.length === 0) {
    throw new ProblemError(
      "initialPopulation must be a non-empty array",
      "E_INVALID_POP",
    )
  }
  if (typeof fitness !== "function") {
    throw new ProblemError("fitness must be a function", "E_INVALID_FITNESS_FN")
  }
  if (typeof mutate !== "function") {
    throw new ProblemError("mutate must be a function", "E_INVALID_MUTATE_FN")
  }
  if (!Number.isInteger(iterations) || iterations < 0) {
    throw new ProblemError(
      "iterations must be a non-negative integer",
      "E_INVALID_ITERATIONS",
    )
  }
  if ((immigration ?? 0) > 0 && typeof createRandom !== "function") {
    throw new ProblemError(
      "immigration > 0 requires a createRandom(rng) function",
      "E_RUN_NEEDS_CREATE_RANDOM",
    )
  }

  // Provide a lightweight problem wrapper; defaults favor mutation-only
  let createIdx = 0
  const problem = {
    createRandom(rng: RNG): T {
      // Priority: caller createRandom if provided
      if (typeof createRandom === "function") return createRandom(rng)
      // Fallback: use initial population first, then mutate a random existing genome
      if (createIdx < initialPopulation.length) {
        return initialPopulation[createIdx++] as T
      }
      const base = initialPopulation[rng.int(initialPopulation.length)] as T
      return mutate(base, rng)
    },
    fitness(g: Readonly<T>): number {
      return fitness(g)
    },
    mutate(g: Readonly<T>, rng: RNG): T {
      return mutate(g, rng)
    },
    crossover(a: Readonly<T>, b: Readonly<T>, rng: RNG): T[] {
      if (typeof crossover === "function") return crossover(a, b, rng)
      // Default: pass-through (no crossover)
      return [a as T]
    },
  }

  // Calculate probabilities ensuring breeding operators sum to 1
  const hasCrossover = typeof crossover === "function"
  const finalCxProb = hasCrossover ? (cxProb ?? DEFAULT_CX_PROB) : (cxProb ?? 0)
  const finalImmigration = immigration ?? 0

  // If mutProb is not provided, calculate it so cx+mut=1 (immigration is separate)
  const finalMutProb = mutProb ?? 1 - finalCxProb

  const gp = new GPLite<T>(problem, {
    popSize: initialPopulation.length,
    generations: iterations,
    cxProb: finalCxProb,
    mutProb: finalMutProb,
    immigration: finalImmigration,
  })
  return gp.run()
}
